import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "../components/Footer";
import { LOGO_DARK } from "../data/assets";

const SECTIONS = [
  {
    id: "who-we-are",
    title: "Who We Are",
    body: [
      "Litisha Foot Care is a mobile foot care clinic providing professional foot health services and home visit appointments across Scotland.",
      "If you have any questions regarding this Privacy Policy or how your information is handled, please contact us through the contact details provided on our website.",
    ],
  },
  {
    id: "information-we-collect",
    title: "Information We Collect",
    body: [
      "We may collect and process the following types of personal information:",
    ],
    groups: [
      {
        heading: "Personal Information",
        items: [
          "Full name",
          "Address",
          "Telephone number",
          "Email address",
          "Date of birth",
          "Emergency contact details",
        ],
      },
      {
        heading: "Health Information",
        intro:
          "To provide safe and appropriate foot care treatment, we may collect relevant medical and health information, including:",
        items: [
          "Medical history",
          "Medication information",
          "Diabetes status",
          "Allergies",
          "Circulation or neurological conditions",
          "Treatment records and clinical notes",
        ],
      },
      {
        heading: "Website Information",
        intro: "When you visit our website, we may automatically collect:",
        items: [
          "IP address",
          "Browser type",
          "Device information",
          "Pages visited",
          "Website usage data through cookies and analytics tools",
        ],
      },
    ],
  },
  {
    id: "how-we-use",
    title: "How We Use Your Information",
    body: ["We use your information to:"],
    list: [
      "Provide safe and effective foot care treatments",
      "Arrange and manage appointments",
      "Communicate with you regarding your care",
      "Maintain clinical records",
      "Respond to enquiries",
      "Improve our website and services",
      "Meet legal and professional obligations",
      "Process payments where applicable",
    ],
    after: ["We only collect information that is necessary for providing our services."],
  },
  {
    id: "lawful-basis",
    title: "Lawful Basis for Processing",
    body: ["Under UK GDPR, we process personal information under the following lawful bases:"],
    list: [
      "Consent",
      "Performance of a contract",
      "Legal obligations",
      "Legitimate interests",
      "Provision of healthcare services",
    ],
    after: [
      "Health-related information is treated as special category data and is handled with additional protection and confidentiality.",
    ],
  },
  {
    id: "data-security",
    title: "Data Storage and Security",
    body: [
      "Litisha Foot Care takes appropriate technical and organisational measures to protect your personal data against unauthorised access, loss, misuse, disclosure, or alteration.",
      "We securely store clinical records and personal information and only retain data for as long as necessary to fulfil legal, professional, and clinical obligations.",
    ],
  },
  {
    id: "sharing",
    title: "Sharing Your Information",
    body: ["We do not sell or rent your personal information to third parties.", "Your information may only be shared when necessary:"],
    list: [
      "With your consent",
      "Where required by law",
      "With healthcare professionals involved in your care",
      "With secure service providers supporting our business operations",
    ],
    after: ["All third parties are expected to respect the security and confidentiality of your information."],
  },
  {
    id: "cookies",
    title: "Cookies",
    body: [
      "Our website may use cookies to improve user experience and monitor website performance.",
      "Cookies are small files stored on your device that help websites function effectively.",
      "You can manage or disable cookies through your browser settings.",
    ],
  },
  {
    id: "your-rights",
    title: "Your Rights",
    body: ["Under data protection laws, you have rights regarding your personal information, including:"],
    list: [
      "The right to access your personal data",
      "The right to request correction of inaccurate information",
      "The right to request deletion of data where applicable",
      "The right to restrict or object to processing",
      "The right to data portability",
      "The right to withdraw consent where consent is relied upon",
    ],
    after: ["Requests relating to your data can be made using the contact details provided on our website."],
  },
  {
    id: "retention",
    title: "Retention of Records",
    body: [
      "We retain clinical and personal records only for as long as necessary to comply with legal, insurance, and professional requirements.",
      "When records are no longer required, they will be securely deleted or destroyed.",
    ],
  },
  {
    id: "third-party",
    title: "Third-Party Links",
    body: [
      "Our website may contain links to external websites. We are not responsible for the privacy practices or content of third-party websites.",
    ],
  },
  {
    id: "changes",
    title: "Changes to This Privacy Policy",
    body: [
      "We may update this Privacy Policy from time to time to reflect changes in legal requirements or our services.",
      "Any updates will be published on this page with a revised effective date.",
    ],
  },
  {
    id: "contact",
    title: "Contact Us",
    body: [
      "If you have any questions about this Privacy Policy or your personal information, please contact Litisha Foot Care using the contact details provided on our website.",
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <div data-testid="privacy-policy-page" className="min-h-screen bg-[#1B0B3A] text-[#F5F2FF]">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#1B0B3A]/85 backdrop-blur-xl border-b border-[#4A2D7A]/60">
        <div className="lfc-container flex items-center justify-between py-4">
          <Link
            to="/"
            data-testid="privacy-back-home"
            className="flex items-center gap-3 group"
          >
            <span className="grid place-items-center w-12 h-12 rounded-2xl bg-[#2D1456] ring-1 ring-[#2BB8B4]/40 p-1.5">
              <img src={LOGO_DARK} alt="Litisha Foot Care" className="w-full h-full object-contain" />
            </span>
            <span className="hidden sm:flex flex-col text-left leading-tight">
              <span className="text-[15px] font-semibold text-white" style={{ fontFamily: "Outfit" }}>
                Litisha Foot Care
              </span>
              <span className="text-[10.5px] uppercase tracking-[0.22em] text-[#5FE6E0]">
                Privacy Policy
              </span>
            </span>
          </Link>
          <Link
            to="/"
            className="lfc-btn-secondary text-[13.5px] py-2.5 px-5"
            data-testid="privacy-home-cta"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.8} />
            Back to home
          </Link>
        </div>
      </header>

      <main className="relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[480px] h-[480px] rounded-full bg-[#2BB8B4]/10 blur-[140px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-[#7B46D9]/15 blur-[120px] pointer-events-none" />

        <div className="lfc-container relative py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Sticky TOC */}
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                <div className="lfc-eyebrow mb-3">Privacy Policy</div>
                <h1 className="lfc-h1 text-[2.4rem] lg:text-[2.8rem]">
                  Your data, handled with care.
                </h1>
                <p className="mt-5 text-[#C2B4E6] leading-relaxed">
                  Effective Date:{" "}
                  <span className="text-white font-medium">May 2026</span>
                </p>
                <div className="lfc-divider-leaf mt-6" />

                <nav className="mt-8 hidden lg:block" data-testid="privacy-toc">
                  <div className="text-[12px] uppercase tracking-[0.22em] text-[#8B7FB5] font-medium mb-4">
                    On this page
                  </div>
                  <ul className="space-y-2.5">
                    {SECTIONS.map((s) => (
                      <li key={s.id}>
                        <a
                          href={`#${s.id}`}
                          className="text-[14px] text-[#C2B4E6] hover:text-[#5FE6E0] transition-colors"
                          data-testid={`toc-${s.id}`}
                        >
                          {s.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </aside>

            {/* Content */}
            <div className="lg:col-span-8 space-y-12">
              <p className="text-lg text-[#C2B4E6] leading-relaxed">
                Litisha Foot Care is committed to protecting and respecting your privacy. This
                Privacy Policy explains how we collect, use, store, and protect your personal
                information when you use our website or contact our mobile foot care clinic.
              </p>
              <p className="text-[16px] text-[#C2B4E6] leading-relaxed -mt-6">
                We handle personal information in accordance with applicable UK data protection
                laws, including the UK General Data Protection Regulation (UK GDPR) and the
                Data Protection Act 2018.
              </p>

              {SECTIONS.map((s) => (
                <section
                  key={s.id}
                  id={s.id}
                  data-testid={`privacy-section-${s.id}`}
                  className="scroll-mt-28"
                >
                  <h2
                    className="text-[26px] lg:text-[28px] font-semibold text-white"
                    style={{ fontFamily: "Outfit" }}
                  >
                    {s.title}
                  </h2>
                  <div className="mt-1 w-12 h-0.5 bg-[#2BB8B4] rounded-full" />

                  <div className="mt-5 space-y-4 text-[15.5px] text-[#E5DEFA] leading-relaxed">
                    {s.body?.map((p, i) => <p key={i}>{p}</p>)}

                    {s.list && (
                      <ul className="space-y-2.5 pl-1">
                        {s.list.map((item) => (
                          <li key={item} className="flex items-start gap-3">
                            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#2BB8B4] shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {s.groups?.map((g) => (
                      <div key={g.heading} className="lfc-card p-6 mt-3">
                        <h3
                          className="text-[17px] font-semibold text-white"
                          style={{ fontFamily: "Outfit" }}
                        >
                          {g.heading}
                        </h3>
                        {g.intro && (
                          <p className="mt-2 text-[14.5px] text-[#C2B4E6]">
                            {g.intro}
                          </p>
                        )}
                        <ul className="mt-3 space-y-2">
                          {g.items.map((item) => (
                            <li key={item} className="flex items-start gap-3 text-[14.5px] text-[#E5DEFA]">
                              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#2BB8B4] shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}

                    {s.after?.map((p, i) => <p key={`a-${i}`}>{p}</p>)}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
